import { useCallback, useEffect, useRef, useState } from 'react';
import SignaturePadLib from 'signature_pad';
import { RotateCcw, PenLine } from 'lucide-react';

interface SignaturePadProps {
  onSign: (dataUrl: string) => void;
  disabled?: boolean;
}

export function SignaturePad({
  onSign,
  disabled = false,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);

  const [isEmpty, setIsEmpty] = useState(true);

  const onSignRef = useRef(onSign);

  useEffect(() => {
    onSignRef.current = onSign;
  }, [onSign]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const pad = padRef.current;

    if (!canvas || !pad) return;

    // Sauvegarde la signature avant le resize
    const data = !pad.isEmpty() ? pad.toData() : [];

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;

    const ctx = canvas.getContext('2d');

    if (ctx) {
      ctx.scale(ratio, ratio);
    }

    // Restaure la signature après le resize
    if (data.length > 0) {
      pad.fromData(data);
      setIsEmpty(false);
    } else {
      pad.clear();
      setIsEmpty(true);
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const pad = new SignaturePadLib(canvas, {
      backgroundColor: 'rgba(255,255,255,0)',
      penColor: '#1a1a2e',
      minWidth: 1.5,
      maxWidth: 3,
    });

    padRef.current = pad;

    pad.addEventListener('beginStroke', () => {
      setIsEmpty(false);
    });

    /**
     * Dès que l'utilisateur termine un tracé,
     * on transmet la signature au SignerForm.
     *
     * Cela ne "valide" PAS définitivement la signature :
     * SignerForm conserve son bouton "Valider la signature".
     */
    pad.addEventListener('endStroke', () => {
      if (pad.isEmpty()) return;

      const dataUrl = pad.toDataURL('image/png');
      onSignRef.current(dataUrl);

      setIsEmpty(false);
    });

    resizeCanvas();

    const observer = new ResizeObserver(() => {
      resizeCanvas();
    });

    if (canvas.parentElement) {
      observer.observe(canvas.parentElement);
    }

    return () => {
      observer.disconnect();
      pad.off();
    };
  }, [resizeCanvas]);

  useEffect(() => {
    const pad = padRef.current;

    if (!pad) return;

    if (disabled) {
      pad.off();
    } else {
      pad.on();
    }
  }, [disabled]);

  const handleClear = () => {
    const pad = padRef.current;

    if (!pad) return;

    pad.clear();
    setIsEmpty(true);

    /**
     * Informe SignerForm que la signature
     * vient d'être supprimée.
     */
    onSignRef.current('');
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        className="relative overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-white"
        style={{ height: 160 }}
      >
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-400">
            <PenLine className="h-6 w-6" />
            <span className="text-sm">Signez ici</span>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="h-full w-full touch-none"
          style={{
            cursor: disabled ? 'not-allowed' : 'crosshair',
          }}
        />

        {disabled && (
          <div className="absolute inset-0 rounded-xl bg-gray-100/60" />
        )}
      </div>

      <div className="flex items-center">
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled || isEmpty}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Effacer
        </button>
      </div>
    </div>
  );
}

export default SignaturePad;